import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import { auth } from '../../firebase/config';
import { formatUtcNaiveInstantForViewer, getViewerTimezone } from '../../utils/dateTime';
import './TransactionHistory.css';

const PAGE_SIZE = 30;

// Chip -> real backend eventType values. Filtered via the `eventType` list query
// param on TransactionAuditController — NOT `category`, which is always the
// hardcoded string "TRANSACTION" for every event this service publishes and
// can't discriminate document vs. signature vs. party sub-types.
const FILTER_CHIPS = [
  { key: 'all', label: 'All Activity', eventTypes: null },
  {
    key: 'doc',
    label: 'Documents',
    eventTypes: [
      'DOCUMENT_UPLOADED', 'DOCUMENT_DELETED', 'FILE_SHARED',
      'FILE_SHARE_TOKEN_GENERATED', 'FILE_SHARE_TOKEN_REVOKED',
      'FILE_DOWNLOADED_VIA_SHARE', 'SENSITIVE_DOCUMENT_DOWNLOADED',
    ],
  },
  {
    key: 'sign',
    label: 'Signatures',
    eventTypes: [
      'DOCUSIGN_ENVELOPE_CREATED', 'DOCUSIGN_ENVELOPE_SENT',
      'DOCUSIGN_ENVELOPE_SIGNED', 'DOCUSIGN_ENVELOPE_VOIDED',
    ],
  },
  {
    key: 'party',
    label: 'Parties',
    eventTypes: ['TRANSACTION_PARTY_ADDED', 'TRANSACTION_PARTY_UPDATED', 'TRANSACTION_PARTY_REMOVED'],
  },
  // No backing eventType exists yet for these two — short-circuited client-side
  // below (empty state) rather than silently falling back to "show everything".
  { key: 'form', label: 'Forms', eventTypes: [] },
  { key: 'role', label: 'Role Changes', eventTypes: [] },
];

// Dot color per chip key, reusing real app tint/solid pairs already used by
// .alp-badge--* in ActivityLogPage.css (not the wireframe's own palette).
const DOT_STYLE = {
  doc: { bg: '#F0EBFF', fg: '#5B47D6' },
  sign: { bg: '#e6f9f0', fg: '#1a8c5b' },
  party: { bg: '#e8f0fb', fg: '#2563eb' },
  form: { bg: '#fff4e5', fg: '#c17d11' },
  role: { bg: '#fde8e8', fg: '#c0392b' },
  system: { bg: '#f4f4f4', fg: '#666666' },
};

function dotKeyForEventType(eventType) {
  for (const chip of FILTER_CHIPS) {
    if (chip.key !== 'all' && chip.eventTypes?.includes(eventType)) return chip.key;
  }
  return 'system';
}

// ActivityLog.createdAt is a bare backend LocalDateTime (UTC digits, no 'Z') —
// render it in the viewer's own profile-state timezone, same as everywhere
// else, rather than the browser/device's timezone.
const formatEventDate = (dateStr) => {
  if (!dateStr) return { date: '—', time: '' };
  const zone = getViewerTimezone();
  const { dateLabel, timeLabel } = formatUtcNaiveInstantForViewer(dateStr, zone);
  if (!dateLabel) return { date: '—', time: '' };
  const isoWithZone = /[Zz]|[+-]\d{2}:\d{2}$/.test(dateStr) ? dateStr : `${dateStr}Z`;
  const eventDayKey = new Date(isoWithZone).toLocaleDateString('en-CA', { timeZone: zone });
  const todayKey = new Date().toLocaleDateString('en-CA', { timeZone: zone });
  return { date: eventDayKey === todayKey ? 'Today' : dateLabel, time: timeLabel };
};

const dateRangeToFromTo = (range) => {
  if (range === 'all') return {};
  const days = range === '7d' ? 7 : range === '90d' ? 90 : 30;
  const from = new Date();
  from.setDate(from.getDate() - days);
  return { from: from.toISOString(), to: new Date().toISOString() };
};

const getRowLabel = (event) => event.metadata?.actionLabel || event.display?.label || event.eventType;

// Per-event-type subtitle for the Transaction Log — mirrors the main
// UserActivityPage.js getEventSubtitle so both audit surfaces answer the same
// "what was acted on?" question from the same metadata keys. Backend's
// AuditMetadataEnricher populates fileName / milestoneName / contingencyName /
// taskTitle on every transaction-scoped event; events that pre-date the
// enricher (or genuinely have no body-level resource name — login, status
// change, etc.) return null and the subtitle row is skipped cleanly.
const getRowSubtitle = (event) => {
  const meta = event.metadata || {};
  const str = (key) => (typeof meta[key] === 'string' && meta[key].length > 0 ? meta[key] : null);
  const arr = (key) => (Array.isArray(meta[key]) ? meta[key] : null);

  switch (event.eventType) {
    case 'TRANSACTION_MILESTONE_CREATED':
    case 'TRANSACTION_MILESTONE_UPDATED':
    case 'TRANSACTION_MILESTONE_COMPLETED':
      return str('milestoneName');

    case 'TRANSACTION_CONTINGENCY_ADDED':
    case 'TRANSACTION_CONTINGENCY_UPDATED':
    case 'TRANSACTION_CONTINGENCY_SATISFIED':
    case 'TRANSACTION_CONTINGENCY_WAIVED':
    case 'TRANSACTION_CONTINGENCY_REMOVED':
      return str('contingencyName');

    case 'TRANSACTION_TASK_CREATED':
    case 'TRANSACTION_TASK_UPDATED':
    case 'TRANSACTION_TASK_COMPLETED':
    case 'TRANSACTION_TASK_CANCELLED':
      return str('taskTitle');

    case 'TRANSACTION_PARTY_ADDED':
      return str('role');

    case 'TRANSACTION_PARTY_UPDATED':
    case 'TRANSACTION_PARTY_REMOVED':
      return str('participantRole');

    case 'TRANSACTION_STATUS_CHANGED': {
      const from = str('fromStatus');
      const to = str('toStatus');
      if (from && to) return `${from} → ${to}`;
      return null;
    }

    case 'DOCUMENT_UPLOADED':
    case 'DOCUMENT_DELETED':
      return str('fileName');

    case 'OFFER_SUBMITTED_VIA_LINK':
    case 'OFFER_DOCUMENTS_ADDED_VIA_LINK': {
      // Bulk upload via the public Offer link — backend enriches with a
      // fileNames array (plus a numeric fileCount string). Prefer the full
      // name list; fall back to count-only on older rows.
      const names = arr('fileNames');
      if (names && names.length > 0) {
        if (names.length === 1) return names[0];
        if (names.length <= 3) return names.join(', ');
        return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
      }
      const count = str('fileCount');
      if (count) return `${count} ${count === '1' ? 'document' : 'documents'}`;
      return null;
    }

    case 'SERVICE_REQUEST_CREATED':
      return str('serviceType');

    case 'CALENDAR_EVENT_CREATED':
      return str('title');

    default:
      return null;
  }
};

const hasDiff = (event) =>
  event.metadata?.oldValue !== undefined && event.metadata?.newValue !== undefined;

const getDiffRows = (event) => {
  if (Array.isArray(event.metadata?.diffs)) return event.metadata.diffs;
  if (hasDiff(event)) {
    return [{
      label: event.metadata?.fieldChanged || 'Value',
      oldValue: event.metadata.oldValue,
      newValue: event.metadata.newValue,
    }];
  }
  return [];
};

const TransactionHistory = ({ transaction, initialChip }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const [activeChip, setActiveChip] = useState(initialChip || 'all');
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [dateRange, setDateRange] = useState('30d');
  const [expandedId, setExpandedId] = useState(null);

  const abortRef = useRef(null);

  useEffect(() => {
    if (initialChip) setActiveChip(initialChip);
  }, [initialChip]);

  const getFirebaseToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (auth.currentUser) {
        auth.currentUser.getIdToken(true).then(resolve).catch(reject);
        return;
      }
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error('Firebase auth timeout'));
      }, 10000);
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        clearTimeout(timeoutId);
        unsubscribe();
        if (user) {
          try {
            const token = await user.getIdToken(true);
            resolve(token);
          } catch (err) {
            reject(err);
          }
        } else {
          reject(new Error('No authenticated user'));
        }
      });
    });
  }, []);

  const fetchHistory = useCallback(async (cursor) => {
    if (!transaction?.id) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const isFirst = !cursor;
    if (isFirst) { setLoading(true); setEvents([]); } else { setLoadingMore(true); }
    setError(null);

    try {
      const firebaseToken = await getFirebaseToken();
      const chip = FILTER_CHIPS.find((c) => c.key === activeChip);
      const { from, to } = dateRangeToFromTo(dateRange);
      const params = { pageSize: PAGE_SIZE, sort: 'desc' };
      if (cursor) params.cursor = cursor;
      if (from) params.from = from;
      if (to) params.to = to;
      if (chip?.eventTypes?.length) params.eventType = chip.eventTypes;

      const response = await apiClient.get(
        API_ENDPOINTS.transactionAuditEvents(transaction.id),
        {
          headers: { Authorization: `Bearer ${firebaseToken}` },
          params,
          signal: controller.signal,
          paramsSerializer: (p) => {
            const sp = new URLSearchParams();
            for (const [key, value] of Object.entries(p)) {
              if (Array.isArray(value)) value.forEach((v) => sp.append(key, String(v)));
              else if (value !== undefined && value !== null) sp.append(key, String(value));
            }
            return sp.toString();
          },
        }
      );
      const visible = (response.data?.events || []).filter((e) => !e.display?.hidden);
      setEvents((prev) => (isFirst ? visible : [...prev, ...visible]));
      setNextCursor(response.data?.nextCursor || null);
      setHasMore(!!response.data?.hasMore);
    } catch (err) {
      if (err.name !== 'AbortError' && err.code !== 'ERR_CANCELED') {
        console.error('Error fetching transaction audit trail:', err);
        setError(err.message || 'Failed to load audit trail');
        if (isFirst) setEvents([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [transaction?.id, getFirebaseToken, activeChip, dateRange]);

  useEffect(() => {
    const chip = FILTER_CHIPS.find((c) => c.key === activeChip);
    if (chip && chip.eventTypes !== null && chip.eventTypes.length === 0) {
      // No backing eventType for this chip yet (Forms / Role Changes) — expected
      // empty-state until Step 2 instrumentation lands, not a fetch error.
      setEvents([]); setLoading(false); setHasMore(false); setNextCursor(null); setError(null);
      return;
    }
    fetchHistory(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchHistory, activeChip]);

  const handleLoadMore = () => {
    if (nextCursor && !loadingMore) fetchHistory(nextCursor);
  };

  const availableRoles = Array.from(
    new Set(events.map((e) => e.metadata?.actorRole).filter(Boolean))
  ).sort();

  const displayedEvents = events.filter((e) => {
    if (roleFilter && (e.metadata?.actorRole || '').toUpperCase() !== roleFilter.toUpperCase()) return false;
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      const hay = [
        e.metadata?.actionLabel, e.display?.label, e.metadata?.actorName,
        e.metadata?.fileName, e.metadata?.description,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const handleExportCsv = () => {
    const header = ['Timestamp', 'Event', 'Actor', 'Role'];
    const csvRows = [
      header.join(','),
      ...displayedEvents.map((e) => [
        `"${e.timestamp || ''}"`,
        `"${(getRowLabel(e) || '').replace(/"/g, '""')}"`,
        `"${(e.metadata?.actorName || '').replace(/"/g, '""')}"`,
        `"${e.metadata?.actorRole || ''}"`,
      ].join(','))
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-trail-${transaction?.id || 'transaction'}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="txn-content-section">
        <div className="txn-history-loading">
          <div className="txn-page-loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="txn-content-section">
      <div className="txn-content-header txn-audit-header">
        <div>
          <h2 className="txn-content-title">Transaction Log</h2>
          <p className="txn-audit-subtitle">Chronological record of activity on this transaction</p>
        </div>
        <button className="txn-audit-export-btn" onClick={handleExportCsv}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          <span>Export</span>
        </button>
      </div>

      <div className="txn-audit-chips-row">
        {FILTER_CHIPS.map((chip) => (
          <button
            key={chip.key}
            type="button"
            className={`txn-audit-chip${activeChip === chip.key ? ' txn-audit-chip--active' : ''}`}
            onClick={() => setActiveChip(chip.key)}
          >
            {chip.label}
          </button>
        ))}
      </div>

      <div className="txn-audit-filters">
        <input
          className="txn-audit-search"
          type="text"
          placeholder="Search this page of events…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <select className="txn-audit-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="">All roles</option>
          {availableRoles.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <select className="txn-audit-select" value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="all">All time</option>
        </select>
      </div>

      {error && (
        <p className="txn-tab-placeholder">Unable to load transaction log right now.</p>
      )}

      {!error && displayedEvents.length === 0 && (
        <p className="txn-tab-placeholder">No activity found for this filter.</p>
      )}

      {!error && displayedEvents.length > 0 && (
        <div className="txn-audit-timeline">
          {displayedEvents.map((event) => {
            const { date, time } = formatEventDate(event.timestamp);
            const dotKey = dotKeyForEventType(event.eventType);
            const dotStyle = DOT_STYLE[dotKey] || DOT_STYLE.system;
            const label = getRowLabel(event);
            const subtitle = getRowSubtitle(event);
            // Prefer the resource-specific name (file name, milestone name,
            // etc.) as the row title — "02_home.png" reads better than
            // "Document uploaded" when the viewer is already scrolling a
            // list of uploads. The generic verb label still appears as a
            // smaller caption below when a subtitle displaces it. Rows
            // without a specific name (login, status changes without diff)
            // keep the generic label as the title.
            const title = subtitle || label;
            const verbCaption = subtitle ? label : null;
            const actorName = event.metadata?.actorName || 'Someone';
            const actorRole = event.metadata?.actorRole || '';
            const diffs = getDiffRows(event);
            const expandable = diffs.length > 0;
            const isExpanded = expandedId === event.eventId;

            return (
              <div className="txn-audit-row" key={event.eventId}>
                <div className="txn-audit-dot" style={{ background: dotStyle.bg, color: dotStyle.fg }}>
                  <span className="txn-audit-dot-inner" />
                </div>
                <div className="txn-audit-body">
                  <div
                    className={`txn-audit-row-head${expandable ? ' txn-audit-row-head--clickable' : ''}`}
                    onClick={() => expandable && setExpandedId(isExpanded ? null : event.eventId)}
                  >
                    <div className="txn-audit-title" title={title}>{title}</div>
                    <div className="txn-audit-when">{date} · {time}</div>
                    {expandable && (
                      <svg
                        className={`txn-audit-chevron${isExpanded ? ' txn-audit-chevron--open' : ''}`}
                        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    )}
                  </div>
                  <div className="txn-audit-meta">
                    {verbCaption && (
                      <span className="txn-audit-verb-caption">{verbCaption}</span>
                    )}
                    <span className="txn-audit-actor">{actorName}</span>
                    {actorRole && <span className="txn-audit-role-pill">{actorRole}</span>}
                  </div>
                  {expandable && isExpanded && (
                    <div className="txn-audit-diff-block">
                      {diffs.map((d, i) => (
                        <div className="txn-audit-diff-row" key={i}>
                          <span className="txn-audit-diff-label">{d.label}</span>
                          <span className="txn-audit-diff-old">{String(d.oldValue ?? '—')}</span>
                          <span className="txn-audit-diff-arrow">→</span>
                          <span className="txn-audit-diff-new">{String(d.newValue ?? '—')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!error && hasMore && (
        <div className="txn-audit-load-more-wrap">
          <button type="button" className="txn-audit-load-more-btn" onClick={handleLoadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load More'}
          </button>
        </div>
      )}

      <div className="txn-audit-legend">
        {FILTER_CHIPS.filter((c) => c.key !== 'all').map((chip) => {
          const s = DOT_STYLE[chip.key];
          return (
            <div className="txn-audit-legend-item" key={chip.key}>
              <span className="txn-audit-legend-swatch" style={{ background: s.fg }} />
              <span>{chip.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TransactionHistory;
